import SubpageLayout from "@/components/SubpageLayout";
import { P, Emphasis, Section, Sub, Bullets, ContactCta, BackLink } from "@/components/Prose";

const Implementering = () => (
  <SubpageLayout overline="VI TILBYDER" title="Implementering" art="/brand/phase-grobund.png" testId="page-service-implementering">
    <Section title="Når traumebevidsthed skal være mere end ny viden">
      <P>
        Vi kan undervise medarbejdere i traumer og traumereaktioner. Men den nye viden skal
        omsættes i en hverdag, hvor lovgivning, interne og eksterne krav, organisatoriske rammer og
        samarbejdet med andre aktører ikke altid trækker i samme retning.
      </P>
      <P>
        Implementering handler derfor om at undersøge, hvor rammer og krav spænder ben, hvor der
        findes handlemuligheder, og hvordan vi kan skabe bedre betingelser for at arbejde
        traumebevidst – også når ikke alle vilkår kan ændres.
      </P>
      <P>
        Dermed handler implementering også om at skabe de bedst mulige betingelser for, at
        medarbejdernes viden og kompetencer kan omsættes til traumebevidst praksis.
      </P>
    </Section>

    <Section title="Fra forståelse til organisatorisk praksis">
      <P>
        I traumebevidst tilgang spørger vi gerne: “Hvad er der sket dig?” Men traumebevidst tilgang
        er langt mere end det.
      </P>
      <P>
        Det afgørende er også, hvordan vi forstår svaret, hvad vi gør med det, og om vi skaber
        rammer, hvor det bliver meningsfuldt og muligt at stille spørgsmålet og for mennesket at
        svare.
      </P>
      <P>
        Det kræver, at viden om traumer, traumereaktioner og veje til recovery får betydning for
        organisationens beslutninger, relationer, politikker, procedurer og daglige praksis.
      </P>
      <P>I vores implementeringsarbejde undersøger vi blandt andet:</P>
      <div className="space-y-10 pt-4">
        <Sub title="Ledelse og organisatoriske prioriteringer">
          <P>
            En ledelse kan efterspørge traumebevidst praksis. Men hvad sker der, hvis medarbejderne
            primært måles på noget, der trækker i en anden retning? Vi kan ønske at skabe tillid og
            gode relationer, men er arbejdet organiseret, bemandingen sammensat og overgangene
            tilrettelagt, så der faktisk er mulighed for kontinuitet og relationsarbejde?
          </P>
          <P>
            Vi arbejder med, hvordan ledelsen kan skabe og beskytte betingelserne for traumebevidst
            praksis – også når tid, økonomi, eksterne krav og drift presser.
          </P>
        </Sub>

        <Sub title="Faglige forståelser og sprog">
          <P>
            De ord og forståelser, vi bruger, påvirker, hvad vi får øje på, og hvordan vi handler.
            Vi undersøger blandt andet, hvordan traumereaktioner forstås og beskrives, og hvordan
            definitioner, sprog og magt kan åbne eller begrænse vores handlemuligheder.
          </P>
        </Sub>

        <Sub title="Arbejdsgange, procedurer og fysiske rammer">
          <P>
            Hvordan opleves organisationens rammer af de mennesker, der møder dem? Giver vores
            procedurer mulighed for fleksibilitet, forudsigelighed og indflydelse? Og hvor kan
            eksempelvis dokumentationskrav, overgange, fysiske omgivelser eller måden, vi
            organiserer kontakten på, utilsigtet skabe utryghed eller afmagt?
          </P>
        </Sub>

        <Sub title="Medarbejdernes kompetencer og betingelser">
          <P>
            Traumebevidst praksis kræver viden, men også faglig sparring, supervision og mulighed
            for refleksion. Vi arbejder med at styrke medarbejdernes faglige sikkerhed og
            handlemuligheder, så ansvaret for at arbejde traumebevidst ikke bliver placeret hos den
            enkelte alene.
          </P>
        </Sub>

        <Sub title="Samarbejde på tværs">
          <P>
            Mennesker bevæger sig mellem organisationer og systemer, som ikke altid arbejder ud fra
            samme forståelse eller stiller forenelige krav. Vi arbejder med fælles sprog,
            sammenhæng i overgange og opmærksomhed på, hvordan forskellige indsatser og systemkrav
            kan understøtte eller modarbejde hinanden.
          </P>
        </Sub>

        <Sub title="Erfaringsfaglighed og reel indflydelse">
          <P>
            Erfaringsfaglighed er en del af vidensgrundlaget for traumebevidst tilgang – ikke et
            supplement. Derfor handler implementering også om, hvem der får mulighed for at
            definere problemer, stille spørgsmål og påvirke beslutninger. Vi arbejder med at skabe
            betingelser for samskabelse mellem forskellige former for viden, frem for alene at
            indhente erfaringer inden for rammer, andre allerede har fastlagt.
          </P>
        </Sub>
      </div>
    </Section>

    <Section title="Hvorfor investere i implementering af traumebevidst tilgang?">
      <P>
        Traumebevidst implementering handler ikke om at gøre alle medarbejdere til
        traumebehandlere. Det handler om at undersøge og udvikle de rammer og forståelser, der
        understøtter og fremmer traumebevidst praksis.
      </P>
      <P>
        Det indebærer også modet til at undersøge, hvordan organisationens egne handlinger og
        rammer utilsigtet kan påføre mennesker skade eller retraumatisering – og arbejde på at
        mindske den risiko.
      </P>
      <P>Det kan blandt andet være relevant, når en organisation ønsker at:</P>
      <Bullets
        items={[
          "forebygge konflikter og situationer, der udvikler sig uhensigtsmæssigt",
          "styrke medarbejdernes faglige sikkerhed og fælles handlemuligheder",
          "forebygge belastning, afmagt og forråelse",
          "skabe bedre betingelser for trivsel og et bæredygtigt arbejdsmiljø",
          "mindske sygefravær og hyppige opsigelser",
          "styrke kontinuitet, samarbejde og kvalitet i indsatsen",
          "mindske risikoen for utilsigtet skade og retraumatisering",
        ]}
      />
      <Emphasis>
        Målet er ikke traumebevidsthed for traumebevidsthedens egen skyld. Målet er, at både de
        mennesker, organisationen er sat i verden for, og de mennesker, der arbejder i den, får
        bedre betingelser.
      </Emphasis>
    </Section>

    <Section title="Hvordan arbejder vi med implementering?">
      <P>
        Vi tager udgangspunkt i den konkrete organisation og dens opgaver, målgrupper og vilkår.
      </P>
      <P>
        Hvad fungerer allerede? Hvor opstår de situationer, hvor medarbejdernes faglige intentioner
        og organisationens rammer ikke hænger sammen? Hvilke forståelser og prioriteringer ligger
        bag? Og hvad kan ændres?
      </P>
      <P>
        Et forløb kan omfatte undervisning, supervision, faglig sparring, ledelsessparring og
        konkret støtte til at udvikle organisatoriske rammer, procedurer og samarbejdsformer.
      </P>
      <P>
        Vi arbejder med både medarbejdere, teams og ledelse, fordi forandringer i praksis ofte
        kræver ændringer på flere niveauer.
      </P>
      <Emphasis>
        Forståelse kan sætte en bevægelse i gang. Implementering handler om at skabe og beskytte
        betingelserne for, at den kan blive til praksis.
      </Emphasis>
    </Section>

    <ContactCta title="Skal vi se på, hvad der giver mening hos jer?">
      <P>
        Organisationer er forskellige, og implementering behøver ikke følge én bestemt model.
        Kontakt os gerne til en uforpligtende samtale om jeres situation, de udfordringer I står
        med, og hvad der kunne give mening hos jer.
      </P>
    </ContactCta>

    <BackLink />
  </SubpageLayout>
);

export default Implementering;
